import { Component, type ReactNode, type ErrorInfo } from "react";
import * as Sentry from "@sentry/react";
import { t } from "@/lib/i18n";

interface AppLayoutErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

class AppLayoutErrorBoundary extends Component<{ children: ReactNode }, AppLayoutErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: Error): AppLayoutErrorBoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppLayout] Caught render error:", error.message, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  handleTryAgain = () => {
    this.setState({ hasError: false, errorMessage: "" });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          aria-live="assertive"
          className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4 p-6"
        >
          <div className="text-center max-w-md">
            <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-orange-600 text-xl font-bold" aria-hidden="true">!</span>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">{t("errors.boundary.title")}</h2>
            <p className="text-sm text-slate-500 mb-4">
              {t("errors.boundary.body")}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={this.handleTryAgain}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-200 transition-colors border border-slate-200"
                data-testid="button-try-again"
              >
                {t("errors.boundary.tryAgain")}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-md hover:bg-orange-600 transition-colors"
                data-testid="button-reload-page"
              >
                {t("errors.boundary.action")}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export { AppLayoutErrorBoundary };
